import axios from 'axios';
import { buildAuthHeaders, readAuthContext } from '@/services/api/_authHeaders';

// Get API base URL from environment variable, fallback to relative path for development
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor — attaches Authorization and X-Hospital-Id headers.
axiosInstance.interceptors.request.use(
  (config) => {
    const authHeaders = buildAuthHeaders();
    for (const [key, value] of Object.entries(authHeaders)) {
      config.headers[key] = value;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

/**
 * Stores the tokens a refresh just issued.
 *
 * localStorage first and unconditionally: it is what `buildAuthHeaders` reads,
 * so the next request must see the new token even if Pinia is not up yet. The
 * store is then brought into step because it is not merely a cache — `logout`
 * revokes whatever refresh token it holds, and rotation has already retired the
 * previous one, so a stale store would revoke a dead token and leave the live
 * one usable for its full 30-day life. `useQueueSocket` reads the access token
 * from the store too.
 */
async function persistRefreshedTokens(accessToken: string, newRefreshToken: string): Promise<void> {
  const stored = readAuthContext() ?? {};
  localStorage.setItem('auth', JSON.stringify({ ...stored, token: accessToken, refreshToken: newRefreshToken }));

  try {
    // Imported lazily: the auth store imports this module, so a static import
    // would be a cycle. By call time both modules are loaded.
    const { useAuthStore } = await import('@/stores/auth');
    useAuthStore().applyRefreshedTokens(accessToken, newRefreshToken);
  } catch {
    // No active Pinia (a refresh that beat app mount). localStorage above
    // already carries the new tokens and rehydration will pick them up.
  }
}

let refreshInFlight: Promise<string | null> | null = null;

async function requestNewTokens(): Promise<string | null> {
  const stored = readAuthContext();
  if (!stored?.refreshToken) return null;

  // Bare axios on purpose — routing this through `axiosInstance` would put the
  // refresh call under the very interceptor that issued it.
  const response = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken: stored.refreshToken });
  const { accessToken, refreshToken: newRefreshToken } = response.data;
  await persistRefreshedTokens(accessToken, newRefreshToken);
  return accessToken;
}

/**
 * Refreshes the session, at most once at a time. Rotation (F-07) revokes the
 * refresh token it is handed, so parallel 401s each refreshing on their own
 * would send the same token twice and every loser would be rejected — logging
 * a perfectly good session out. Callers share the one in-flight attempt.
 *
 * Resolves to the new access token, or null when there is nothing to refresh
 * with. Rejects if the refresh itself was refused.
 */
export function refreshAuthTokens(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = requestNewTokens().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

function redirectToLogin(): void {
  localStorage.removeItem('auth');
  window.location.href = '/login';
}

// Response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Auth endpoints handle their own 401s. The login form needs to render
    // "wrong password" inline; if we trigger the refresh-or-redirect branch
    // below the page reloads and the error message is wiped. Refresh failures
    // already bypass via `_retry`, but the login call has no retry flag — we
    // need an explicit URL check.
    const requestUrl: string = originalRequest?.url ?? '';
    const isAuthEndpoint = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/refresh');

    // If 401 and not already retried, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      try {
        const accessToken = await refreshAuthTokens();

        if (!accessToken) {
          // Nothing to refresh with — the session is over.
          redirectToLogin();
          return Promise.reject(error);
        }

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        redirectToLogin();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
export { API_BASE_URL };
